import { getDistance, isPointInCircle, isPointInPolygon } from '../utils/geometry.js';

/**
 * Advanced Fare Calculator based on Geofencing and Custom Routes.
 * Priority Order:
 * 1. Custom Route (Forfait) - Fixed price between two specific points (with radius tolerance).
 * 2. Zone to Zone - Fixed price between two defined zones.
 * 3. Zone Specific - Mixed calculation (Base + Km + Time) if origin or destination is in a zone.
 * 4. Fallback - Standard calculation using global settings.
 * 
 * @param {object} tripDetails - { distance: meters, time: seconds, origin: {lat, lng}, dest: {lat, lng} }
 * @param {object} config - The full pricing configuration { zones, customRoutes, zoneToZoneRules, globalRates }
 * @param {object} carTypeDetails - Details of the selected car type (used for fallback or multipliers)
 * @returns {object} { totalCost, grandTotal, convenience_fees, method: 'custom_route'|'zone_fixed'|'zone_mixed'|'fallback' }
 */

export const AdvancedFareCalculator = (tripDetails, config, carTypeDetails, instructionData) => {
    const { distance, time, origin, dest } = tripDetails;
    const { zones, customRoutes, zoneToZoneRules, globalRates } = config || {};

    const rawTz = config?.timezone || config?.timeZone || config?.globalRates?.timezone || tripDetails?.timezone || carTypeDetails?.timezone;
    const appTimezone = (rawTz && typeof rawTz === 'string' && rawTz.trim()) ? rawTz.trim() : "Europe/Paris";

    // Safety check for critical data
    if (!origin || !dest) {
        return calculateFallback(distance, time, globalRates || carTypeDetails, new Date(), instructionData, appTimezone);
    }

    // --- 0. HOURLY BOOKING (Mise à disposition) ---
    if (instructionData?.tripType === "hourly") {
        const hours = parseFloat(instructionData.hours) || parseFloat(instructionData.options?.hours) || 1;
        let hourlyTotal = 0;

        // Priority 1: Hourly Packages
        const pkg = (carTypeDetails.hourly_packages || []).find((p) => parseFloat(p.hours) === hours);

        if (pkg) {
            hourlyTotal = parseFloat(pkg.price);
        } else {
            // Priority 2: Standard Hourly Rate (Prefer rate_per_hour edited in Admin UI)
            const hourlyRate = parseFloat(
                (carTypeDetails.rate_per_hour !== undefined && carTypeDetails.rate_per_hour !== null && carTypeDetails.rate_per_hour !== "")
                    ? carTypeDetails.rate_per_hour
                    : (carTypeDetails.hourly_rate || 0)
            );
            hourlyTotal = hourlyRate * hours;
        }

        return formatResult(hourlyTotal, 'hourly', `${hours}h Hourly Booking`, { isNightPrice: true });
    }



    // Helper to get price for specific car
    const getVehiclePrice = (node, car, defaultPriceKey = 'price') => {
        if (node.vehiclePrices) {
            if (car.id && node.vehiclePrices[car.id]) return parseFloat(node.vehiclePrices[car.id]);
            if (car.name && node.vehiclePrices[car.name]) return parseFloat(node.vehiclePrices[car.name]);
        }
        return parseFloat(node[defaultPriceKey] || 0);
    };

    const getVehicleParams = (node, car) => {
        if (node.vehicleParams) {
            if (car.id && node.vehicleParams[car.id]) return node.vehicleParams[car.id];
            if (car.name && node.vehicleParams[car.name]) return node.vehicleParams[car.name];
        }
        return node.param;
    };

    // --- 1. Identify Candidate Zones (Prerequisite for Zone-to-Zone) ---
    const startZones = [];
    const endZones = [];

    if (zones) {
        for (const zoneId in zones) {
            const zone = zones[zoneId];
            if (!zone.isActive) continue;

            // Check Origin
            const isStartInside = zone.type === 'circle'
                ? isPointInCircle(origin, zone.center, zone.radius)
                : isPointInPolygon(origin, zone.coordinates);

            if (isStartInside) {
                startZones.push({ id: zoneId, ...zone });
            }

            // Check Dest
            const isEndInside = zone.type === 'circle'
                ? isPointInCircle(dest, zone.center, zone.radius)
                : isPointInPolygon(dest, zone.coordinates);

            if (isEndInside) {
                endZones.push({ id: zoneId, ...zone });
            }
        }
    }

    // --- 1.2 PRIORITY SORTING ---
    // Higher specificity wins: Special > Cap Ferret > Communes > Grid (Web)
    const categoryPriority = {
        "Special": 1,
        "Cap Ferret": 2,
        "Bordeaux Métropole Proche": 3,
        "Gironde": 3,
        "Bordeaux-Grille": 4
    };

    const zoneSorter = (a, b) => {
        const pA = categoryPriority[a.category] || 99;
        const pB = categoryPriority[b.category] || 99;
        if (pA !== pB) return pA - pB;
        if (a.radius && b.radius) return a.radius - b.radius;
        return 0;
    };

    startZones.sort(zoneSorter);
    endZones.sort(zoneSorter);

    // Determine if this is a night booking
    const nightStart = carTypeDetails?.night_start || "19:00";
    const nightEnd = carTypeDetails?.night_end || "07:00";
    const pickupDate = instructionData?.date || new Date();
    const isNight = isNightTime(pickupDate, nightStart, nightEnd, appTimezone, carTypeDetails);
    // Check if trip contains intermediate stops (waypoints)
    const hasWaypoints = Boolean(
        (Array.isArray(instructionData?.waypoints) && instructionData.waypoints.length > 0) ||
        (Array.isArray(tripDetails?.waypoints) && tripDetails.waypoints.length > 0)
    );

    // --- 2. Zone to Zone (Fixed) - PRIORITY 1 (Bypassed if intermediate stops exist) ---
    if (!hasWaypoints && zoneToZoneRules && startZones.length > 0 && endZones.length > 0) {
        // [OPTIMIZATION] Build a fast lookup map for match performance O(1)
        const ruleMap = new Map();

        if (Array.isArray(zoneToZoneRules)) {
            zoneToZoneRules.forEach(r => {
                const from = r.from || r.originZone;
                const to = r.to || r.destZone;
                if (from && to) ruleMap.set(`${from}_${to}`, r);
            });
        } else {
            Object.values(zoneToZoneRules).forEach(r => {
                const from = r.from || r.originZone;
                const to = r.to || r.destZone;
                if (from && to) ruleMap.set(`${from}_${to}`, r);
            });
        }

        const getRulePrice = (rule, isNightForLeg) => {
            let p;
            if (isNightForLeg) {
                if (rule.nightVehiclePrices && carTypeDetails.id && rule.nightVehiclePrices[carTypeDetails.id]) {
                    p = parseFloat(rule.nightVehiclePrices[carTypeDetails.id]);
                } else if (rule.nightVehiclePrices && carTypeDetails.name && rule.nightVehiclePrices[carTypeDetails.name]) {
                    p = parseFloat(rule.nightVehiclePrices[carTypeDetails.name]);
                } else if (rule.nightPrice) {
                    p = parseFloat(rule.nightPrice);
                } else {
                    p = getVehiclePrice(rule, carTypeDetails);
                }
            } else {
                p = getVehiclePrice(rule, carTypeDetails);
            }
            return p;
        };

        const getReversePrice = (rule, isNightForLeg) => {
            let p;
            if (isNightForLeg) {
                // Check new explicit Reverse Night Price fields
                if (rule.reverseNightVehiclePrices && carTypeDetails.id && rule.reverseNightVehiclePrices[carTypeDetails.id]) {
                    p = parseFloat(rule.reverseNightVehiclePrices[carTypeDetails.id]);
                } else if (rule.reverseNightPrice) {
                    p = parseFloat(rule.reverseNightPrice);
                } else {
                    // Fallback to searching for the reverse rule if no explicit field
                    const reverseRule = ruleMap.get(`${rule.to}_${rule.from}`);
                    if (reverseRule) p = getRulePrice(reverseRule, true);
                }
            } else {
                // Same for day price
                if (rule.reverseVehiclePrices && carTypeDetails.id && rule.reverseVehiclePrices[carTypeDetails.id]) {
                    p = parseFloat(rule.reverseVehiclePrices[carTypeDetails.id]);
                } else if (rule.reversePrice) {
                    p = parseFloat(rule.reversePrice);
                } else {
                    const reverseRule = ruleMap.get(`${rule.to}_${rule.from}`);
                    if (reverseRule) p = getRulePrice(reverseRule, false);
                }
            }
            return p;
        };

        const isRoundTrip = instructionData?.isRoundTrip || instructionData?.returnTrip?.enabled;

        // Check combinations (usually few startZones and few endZones)
        for (const sz of startZones) {
            for (const ez of endZones) {
                const rule = ruleMap.get(`${sz.id}_${ez.id}`);
                if (rule) {
                    let total = getRulePrice(rule, isNight);

                    if (isRoundTrip && !isNaN(total)) {
                        const returnDate = instructionData.returnTrip?.scheduledAt || pickupDate;
                        const isReturnNight = isNightTime(returnDate, nightStart, nightEnd, appTimezone, carTypeDetails);
                        let returnLegPrice = getReversePrice(rule, isReturnNight);

                        if (!isNaN(returnLegPrice)) {
                            // Apply return discount if configured
                            const discount = parseFloat(carTypeDetails.return_discount || 0);
                            if (discount > 0) {
                                returnLegPrice = returnLegPrice * (1 - (discount / 100));
                            }
                            total += returnLegPrice;
                        }
                    }

                    if (!isNaN(total)) {
                        return formatResult(total, 'zone_fixed', `${sz.name} -> ${ez.name}${isRoundTrip ? ' (A/R)' : ''}`, {
                            isNightPrice: isNight,
                            isRoundTrip: isRoundTrip
                        });
                    }
                }
            }
        }
    }

    // --- 3. Custom Route Check (Forfaits) - PRIORITY 2 (Bypassed if intermediate stops exist) ---
    if (!hasWaypoints && customRoutes) {
        for (const routeId in customRoutes) {
            const route = customRoutes[routeId];
            if (!route.isActive) continue;

            const isStartMatch = isPointInCircle(origin, route.startPoint, route.startPoint.radius);
            const isEndMatch = isPointInCircle(dest, route.endPoint, route.endPoint.radius);

            // Check Forward Direction
            if (isStartMatch && isEndMatch) {
                const price = getVehiclePrice(route, carTypeDetails);
                if (!isNaN(price)) return formatResult(price, 'custom_route', route.name);
            }

            // Check Reverse Direction (if supported)
            if (route.bidirectional) {
                const isReverseStart = isPointInCircle(origin, route.endPoint, route.endPoint.radius);
                const isReverseEnd = isPointInCircle(dest, route.startPoint, route.startPoint.radius);
                if (isReverseStart && isReverseEnd) {
                    const price = getVehiclePrice(route, carTypeDetails);
                    if (!isNaN(price)) return formatResult(price, 'custom_route', route.name);
                }
            }
        }
    }

    // --- 4. Zone Mixed Calculation (Fallback to Zone Rates) ---
    // Deterministic Date for Pricing (Rush Hour / Night Rates)
    const pricingDate = (instructionData && instructionData.date) ? new Date(instructionData.date) : new Date();

    const sortedStartZones = startZones.sort((a, b) => {
        if (a.type === 'circle' && b.type === 'circle') return a.radius - b.radius;
        return 0;
    });

    if (sortedStartZones.length > 0) {
        const zone = sortedStartZones[0];
        const params = getVehicleParams(zone, carTypeDetails);
        if (params) {
            const isReturnLeg = instructionData?.isRoundTrip === true || instructionData?.isReturn === true;
            return calculateMixed(distance, time, params, 'zone_mixed_origin', pricingDate, carTypeDetails, isReturnLeg, appTimezone);
        }
    }

    // --- 5. AI Dynamic Pricing (Last Priority before Fallback) ---
    // KILL-SWITCH: Check if AI Pricing is globally enabled
    const aiEnabled = config && config.aiPricingEnabled !== false; // Default true if undefined

    if (aiEnabled && instructionData && instructionData.isAiPricing && instructionData.rateOverride) {
        console.log(`[AdvancedFareCalculator] Using AI Dynamic Pricing. Zone: ${instructionData.zoneId}`);

        const aiRatePerKm = parseFloat(instructionData.rateOverride);
        const standardRatePerHour = parseFloat(carTypeDetails.rate_per_hour || 0);

        // 1. Base Distance + Time
        let aiBase = (aiRatePerKm * (distance / 1000)) + (standardRatePerHour * (time / 3600));

        // 2. Add Base Fare
        if (carTypeDetails.base_fare > 0) {
            aiBase += parseFloat(carTypeDetails.base_fare);
        }

        // 3. Min Fare Override
        const aiMinFare = parseFloat(instructionData.minFareOverride || carTypeDetails.min_fare || 0);
        let total = aiBase > aiMinFare ? aiBase : aiMinFare;

        return formatResult(total, 'ai_pricing', `AI Zone: ${instructionData.zoneId} (Rate: ${aiRatePerKm})`);
    }

    // --- 6. Fallback (Global/Standard Rates) ---
    // [FIX] Use carTypeDetails by default. Merge with globalRates if needed, 
    // but specific car rates must override global settings.
    const effectiveRates = { ...(globalRates || {}), ...carTypeDetails };

    // [FIX] Pass distance in KM to fallback logic
    return calculateFallback(distance / 1000, time, effectiveRates, pricingDate, instructionData, appTimezone);
};

// --- Rush Hour Configuration ---
const RUSH_HOUR_CONFIG = {
    enabled: true,
    multiplier: 1.5, // 1.5x on Time Rate
    slots: [
        { start: '07:30', end: '09:30' },
        { start: '16:00', end: '19:00' }
    ],
    days: [1, 2, 3, 4, 5] // Mon-Fri
};

const isRushHour = (date, tz = "Europe/Paris") => {
    if (!RUSH_HOUR_CONFIG.enabled) return false;

    const targetTz = (tz && typeof tz === 'string' && tz.trim()) ? tz.trim() : "Europe/Paris";
    const weekdayShort = date.toLocaleDateString("en-US", { timeZone: targetTz, weekday: "short" });
    const dayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
    const day = dayMap[weekdayShort];
    
    if (!RUSH_HOUR_CONFIG.days.includes(day)) return false;

    const timeStr = date.toLocaleTimeString("en-GB", {
        timeZone: targetTz,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
    });

    return RUSH_HOUR_CONFIG.slots.some(slot => timeStr >= slot.start && timeStr <= slot.end);
};

// --- Helper Functions ---

const isNightTime = (pickupDate, nightStart = "19:00", nightEnd = "07:00", tz = "Europe/Paris", carTypeDetails = null) => {
    if (!pickupDate) return false;

    const date = typeof pickupDate === 'string' ? new Date(pickupDate) : pickupDate;
    const targetTz = (tz && typeof tz === 'string' && tz.trim()) ? tz.trim() : "Europe/Paris";
    const localTimeStr = date.toLocaleTimeString("en-GB", {
        timeZone: targetTz,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
    });

    const [hours, minutes] = localTimeStr.split(":").map(Number);
    const currentTime = hours * 60 + minutes;

    let startTime = 19 * 60; // Default 19:00
    let endTime = 7 * 60;   // Default 07:00

    if (carTypeDetails?.night_surge_details?.start && carTypeDetails?.night_surge_details?.end) {
        const [startHour, startMin] = carTypeDetails.night_surge_details.start.split(":").map(Number);
        const [endHour, endMin] = carTypeDetails.night_surge_details.end.split(":").map(Number);
        startTime = startHour * 60 + startMin;
        endTime = endHour * 60 + endMin;
    } else if (nightStart && nightEnd) {
        const [startHour, startMin] = nightStart.split(":").map(Number);
        const [endHour, endMin] = nightEnd.split(":").map(Number);
        startTime = startHour * 60 + startMin;
        endTime = endHour * 60 + endMin;
    }

    if (startTime > endTime) {
        return currentTime >= startTime || currentTime < endTime;
    } else {
        return currentTime >= startTime && currentTime < endTime;
    }
};

const calculateTieredDistanceCost = (distanceKm, tiers, isRoundTrip) => {
    if (!tiers || !Array.isArray(tiers) || tiers.length === 0) {
        return null;
    }

    // Sort tiers by maxDistance ascending
    const sortedTiers = [...tiers].sort((a, b) =>
        parseFloat(a.maxDistance) - parseFloat(b.maxDistance)
    );

    let applicableTier = null;
    for (const tier of sortedTiers) {
        if (distanceKm <= parseFloat(tier.maxDistance)) {
            applicableTier = tier;
            break;
        }
    }

    // If distance exceeds all tiers, use the last (highest) tier
    if (!applicableTier) {
        applicableTier = sortedTiers[sortedTiers.length - 1];
    }

    // Determine Rate: use returnRate only if we are in a round_trip context
    const ratePerKm = isRoundTrip
        ? parseFloat(applicableTier.returnRate || applicableTier.oneWayRate || 0)
        : parseFloat(applicableTier.oneWayRate || 0);

    return ratePerKm * distanceKm;
};

const calculateMixed = (distanceMeter, timeSeconds, rates, method, pricingDate = new Date(), carTypeDetails = null, isRoundTrip = false, tz = "Europe/Paris") => {
    const distanceKm = distanceMeter / 1000;
    const timeHours = timeSeconds / 3600;

    // [FIX] Use the common isNightTime helper instead of hardcoded 19-07 logic
    // This ensures consistency across all pricing modes and respects carType settings
    const isNight = isNightTime(pricingDate, undefined, undefined, tz, carTypeDetails);
    
    const targetTz = (tz && typeof tz === 'string' && tz.trim()) ? tz.trim() : "Europe/Paris";
    const weekdayShort = pricingDate.toLocaleDateString("en-US", { timeZone: targetTz, weekday: "short" });
    const isSunday = weekdayShort === 'Sun';

    const kmRate = (isNight || isSunday) ? (rates.km_rate_night || rates.km_rate) : rates.km_rate;

    // Rush Hour Logic
    const rushHourActive = isRushHour(pricingDate, targetTz);
    const timeMultiplier = rushHourActive ? RUSH_HOUR_CONFIG.multiplier : 1;

    // Calculate Components
    let distanceCost = 0;
    const hasTiers = carTypeDetails && carTypeDetails.distance_tiers && 
        (Array.isArray(carTypeDetails.distance_tiers) ? carTypeDetails.distance_tiers.length > 0 : Object.keys(carTypeDetails.distance_tiers).length > 0);

    if (hasTiers) {
        // [FIX] Use distance tiers if they exist, even if we are in a mixed zone scenario
        distanceCost = calculateTieredDistanceCost(distanceKm, carTypeDetails.distance_tiers, isRoundTrip);
    } else {
        distanceCost = distanceKm * parseFloat(kmRate || 0);
    }

    // [FIX] Time factor completely removed as per business requirements
    const timeCost = 0;

    let total = parseFloat(rates.base_fare || 0) + distanceCost + timeCost;

    // Min Fare Check
    if (rates.min_fare && total < parseFloat(rates.min_fare)) {
        total = parseFloat(rates.min_fare);
    }

    const details = rushHourActive
        ? `Zone Rates Applied (Traffic ${RUSH_HOUR_CONFIG.multiplier}x)`
        : 'Zone Rates Applied';

    return formatResult(total, method, details);
};

const calculateFallback = (distanceKm, timeSeconds, rates, pricingDate = new Date(), instructionData = {}, tz = "Europe/Paris") => {
    if (!rates) return formatResult(0, 'error', 'No Rates Available');

    const timeHours = timeSeconds / 3600;

    // 1. Try Distance Tiers
    const isRoundTrip = instructionData?.isRoundTrip === true || instructionData?.isReturn === true;
    let distanceCost = calculateTieredDistanceCost(distanceKm, rates.distance_tiers, isRoundTrip);

    // 2. Fallback to standard rate per km if no tiers match
    if (distanceCost === null) {
        const kmRate = parseFloat(rates.rate_per_unit_distance || 0);
        distanceCost = distanceKm * kmRate;
    }

    // 3. Time Cost with Rush Hour
    const rushHourActive = isRushHour(pricingDate, tz);
    const timeMultiplier = rushHourActive ? RUSH_HOUR_CONFIG.multiplier : 1;
    const timeRate = parseFloat(rates.rate_per_hour || 0) * timeMultiplier;
    const timeCost = timeHours * timeRate;

    // 4. Sum up with Base Fare
    const baseFare = parseFloat(rates.base_fare || 0);
    let total = baseFare + distanceCost + timeCost;

    // 5. Min Fare Check
    const minFare = parseFloat(rates.min_fare || 0);
    if (total < minFare) {
        total = minFare;
    }

    const details = rushHourActive
        ? `Standard Rates (Traffic ${RUSH_HOUR_CONFIG.multiplier}x)`
        : 'Standard Rates';

    return formatResult(total, 'fallback', details);
};

const formatResult = (amount, method, details, traceOverride = {}) => {
    const total = parseFloat(amount);
    return {
        totalCost: parseFloat(total.toFixed(2)),
        grandTotal: parseFloat(total.toFixed(2)),
        convenience_fees: 0,
        calculationMethod: method,
        calculationDetails: details,
        pricingTrace: { // Add basic trace for frontend debug if needed
            method,
            details,
            total,
            ...traceOverride // [FIX] allow overrides like isNightPrice
        }
    };
};
